import bnTranslations from './bn.json';
import enTranslations from './en.json';

export type Language = 'bn' | 'en';

export interface Translations {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

class I18n {
  private currentLang: Language;
  private translations: Record<Language, Translations>;
  private listeners: Set<() => void>;

  constructor() {
    this.currentLang = 'en';
    this.translations = {
      bn: bnTranslations,
      en: enTranslations,
    };
    this.listeners = new Set();

    // Only access localStorage in browser
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('chocolate_vibes_lang') as Language;
      if (stored && (stored === 'bn' || stored === 'en')) {
        this.currentLang = stored;
      }
      this.updateDocumentLang();
    }
  }

  private updateDocumentLang() {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = this.currentLang;
      document.documentElement.dir = 'ltr';
    }
  }

  t(key: string, params?: Record<string, string | number>): string {
    let translation = this.translations[this.currentLang]?.[key];

    if (!translation) {
      // Fallback to English
      translation = this.translations.en?.[key] || key;
    }

    if (Array.isArray(translation)) {
      return translation[0] || key; // Fallback for arrays used as string
    }

    // Replace parameters
    if (params && typeof translation === 'string') {
      Object.entries(params).forEach(([paramKey, value]) => {
        translation = (translation as string).replace(`{{${paramKey}}}`, String(value));
      });
    }

    return translation as string;
  }

  getObject<T>(key: string): T {
    let value = this.translations[this.currentLang]?.[key];
    if (!value) {
      value = this.translations.en?.[key];
    }
    return value as T;
  }

  setLanguage(lang: Language) {
    this.currentLang = lang;
    if (typeof window !== 'undefined') {
      localStorage.setItem('chocolate_vibes_lang', lang);
    }
    this.updateDocumentLang();
    this.notifyListeners();
  }

  getLanguage(): Language {
    return this.currentLang;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }
}

// Create singleton instance
export const i18n = new I18n();

// React hook for using i18n
import { useState, useEffect, useMemo } from 'react';

export function useI18n() {
  const [lang, setLang] = useState(i18n.getLanguage());

  useEffect(() => {
    return i18n.subscribe(() => setLang(i18n.getLanguage()));
  }, []);

  return useMemo(() => ({
    t: i18n.t.bind(i18n),
    getObject: i18n.getObject.bind(i18n),
    setLanguage: i18n.setLanguage.bind(i18n),
    currentLang: lang,
  }), [lang]);
}
